import { useState, useEffect, useRef, useCallback } from "react";
/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { useLocation2 } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { getStatusConfig } from "../lib/statusColors";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, User, Car, MapPin,
  Phone, Mail, X, Check, AlertTriangle, Edit, Trash2, Ban, Save, UserX, Upload, Image,
  Users, Columns3, Grid3X3, Search, Plus
} from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, addWeeks, subWeeks } from "date-fns";
import { hu } from "date-fns/locale";
import { 
  requestNotificationPermission,
  notifyNewBooking 
} from "../services/notificationService";
import { useUiVariant } from "../hooks/useUiVariant";
import { studioPageRootClass } from "../lib/studioLayout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LOCATIONS = ["all", "Debrecen", "Budapest"];
// Build STATUS_COLORS from unified config; add lemondta alias for legacy data
const _buildStatusColors = () => {
  const statuses = ["foglalt","visszaigazolva","folyamatban","kesz","lemondva","nem_jott_el","torolt"];
  const map = {};
  statuses.forEach(s => {
    const c = getStatusConfig(s);
    map[s] = `${c.bg} ${c.text} ${c.border}`;
  });
  // Legacy alias used in some older bookings
  map["lemondta"] = map["lemondva"];
  return map;
};
const STATUS_COLORS = _buildStatusColors();

// Filter button labels — only canonical statuses (no lemondta alias to avoid duplicate button)
const STATUS_LABELS = {
  foglalt: "Foglalt",
  visszaigazolva: "Visszaigazolva",
  folyamatban: "Folyamatban",
  kesz: "Kész",
  lemondva: "Lemondva",
  nem_jott_el: "Nem jött el",
  torolt: "Törölve",
};

// Worker colors for column headers
const WORKER_COLORS = [
  "from-blue-500/20 to-cyan-500/20",
  "from-purple-500/20 to-pink-500/20",
  "from-green-500/20 to-emerald-500/20",
  "from-orange-500/20 to-yellow-500/20",
  "from-red-500/20 to-rose-500/20",
  "from-indigo-500/20 to-violet-500/20"
];

const Calendar = () => {
  const ui = useUiVariant();
  const { selectedLocation: globalLocation } = useLocation2();
  const [view, setView] = useState("week"); // week, month, day, list
  const [viewMode, setViewMode] = useState("standard"); // standard, workers (per-worker columns)
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [services, setServices] = useState([]);
  const [location, setLocation] = useState(globalLocation || "all");
  const [selectedWorker, setSelectedWorker] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Drag & Drop state
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { date, hour }
  const [showBlacklistDialog, setShowBlacklistDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [blacklistReason, setBlacklistReason] = useState("");
  const [blacklistImages, setBlacklistImages] = useState([]);
  const [uploadingBlacklistImage, setUploadingBlacklistImage] = useState(false);
  const blacklistFileRef = useRef(null);
  const previousBookingIds = useRef(new Set());

  // ── Új foglalás dialógus ──
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [newBookingSubmitting, setNewBookingSubmitting] = useState(false);
  const [nbSlots, setNbSlots] = useState([]);
  const [nbLoadingSlots, setNbLoadingSlots] = useState(false);
  const [newBooking, setNewBooking] = useState({
    customer_name: "",
    phone: "",
    email: "",
    plate_number: "",
    car_type: "",
    service_id: "",
    worker_id: "",
    notes: "",
    date: "",
    time_slot: "",
    nb_location: "Debrecen",
    is_shared_work: false,
    shared_worker_id: "",
  });

  const calendarEffectiveLocation = location !== "all" ? location : "Debrecen";

  const filterWorkersBookingRules = (list) => {
    if (!list?.length) return [];
    const loc = newBooking.nb_location || calendarEffectiveLocation;
    if (loc !== "Debrecen") return list;
    const onlyBA = list.filter((w) => /bence|attila/i.test(w.name || ""));
    const svc = services.find((s) => s.service_id === newBooking.service_id);
    const nm = ((svc?.name || "") + (svc?.category || "")).toLowerCase();
    const isPolish = nm.includes("polír") || nm.includes("polir") || nm.includes("fényszór") || nm.includes("fenyszor");
    if (isPolish) return onlyBA.filter((w) => /attila/i.test(w.name || ""));
    return onlyBA;
  };

  const openNewBookingDialog = () => {
    const d =
      view === "day"
        ? format(currentDate, "yyyy-MM-dd")
        : format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
    setNewBooking((prev) => ({
      ...prev,
      date: d,
      nb_location: location !== "all" ? location : "Debrecen",
      time_slot: "",
      worker_id: "",
      shared_worker_id: "",
      is_shared_work: false,
    }));
    setShowNewBooking(true);
  };

  useEffect(() => {
    if (!showNewBooking || !newBooking.date || !newBooking.nb_location) return;
    const svc = services.find((s) => s.service_id === newBooking.service_id);
    const duration = svc?.duration || 60;
    setNbLoadingSlots(true);
    axios
      .get(`${API}/bookings/available-slots`, {
        params: { location: newBooking.nb_location, date: newBooking.date, duration },
        withCredentials: true,
      })
      .then((r) => {
        setNbSlots(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => setNbSlots([]))
      .finally(() => setNbLoadingSlots(false));
  }, [showNewBooking, newBooking.date, newBooking.nb_location, newBooking.service_id, services]);

  const submitNewBooking = async () => {
    if (!newBooking.customer_name?.trim() || !newBooking.plate_number?.trim() || !newBooking.email?.trim() || !newBooking.phone?.trim()) {
      toast.error("Töltsd ki a kötelező mezőket (név, rendszám, email, telefon).");
      return;
    }
    if (!newBooking.service_id || !newBooking.date || !newBooking.time_slot) {
      toast.error("Válassz szolgáltatást, dátumot és időpontot.");
      return;
    }
    const svc = services.find((s) => s.service_id === newBooking.service_id);
    const sharedIds =
      newBooking.nb_location === "Debrecen" &&
      newBooking.is_shared_work &&
      newBooking.worker_id &&
      newBooking.shared_worker_id &&
      newBooking.worker_id !== newBooking.shared_worker_id
        ? [newBooking.worker_id, newBooking.shared_worker_id]
        : [];
    const body = {
      customer_name: newBooking.customer_name.trim(),
      car_type: newBooking.car_type || "",
      plate_number: newBooking.plate_number.trim().toUpperCase(),
      email: newBooking.email.trim(),
      phone: newBooking.phone.trim(),
      service_id: newBooking.service_id,
      service_name: svc?.name || "",
      price: Number(svc?.price) || 0,
      duration: svc?.duration || 60,
      location: newBooking.nb_location,
      date: newBooking.date,
      time_slot: newBooking.time_slot,
      notes: newBooking.notes || "",
      worker_id: newBooking.worker_id || undefined,
      is_shared_work: sharedIds.length === 2,
      shared_worker_ids: sharedIds.length ? sharedIds : undefined,
    };
    setNewBookingSubmitting(true);
    try {
      const idemKey = `cal_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      await axios.post(`${API}/bookings`, body, {
        withCredentials: true,
        headers: { "Idempotency-Key": idemKey },
      });
      toast.success("Foglalás létrehozva");
      setShowNewBooking(false);
      fetchData();
      window.dispatchEvent(new CustomEvent("xclean:data-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hiba a foglalás mentésekor");
    } finally {
      setNewBookingSubmitting(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Request notification permission
    requestNotificationPermission();
  }, [currentDate, location, view]);

  // Polling for new bookings every 30 seconds
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      try {
        const dateFrom = format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
        const dateTo = format(addDays(new Date(), 30), "yyyy-MM-dd");
        const res = await axios.get(`${API}/bookings?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true });
        
        const newBookings = res.data || [];
        const currentIds = new Set(newBookings.map(b => b.booking_id));
        
        // Check for new bookings
        newBookings.forEach(booking => {
          if (!previousBookingIds.current.has(booking.booking_id) && previousBookingIds.current.size > 0) {
            // New booking detected!
            notifyNewBooking(booking);
            toast.success(`Új foglalás: ${booking.customer_name} - ${booking.plate_number}`);
          }
        });
        
        previousBookingIds.current = currentIds;
      } catch (e) {
        // Silent fail for polling
      }
    }, 30000); // Poll every 30 seconds
    
    return () => clearInterval(pollInterval);
  }, []);

  // Global realtime refresh (dispatched from App-level SSE connection)
  useEffect(() => {
    const handleDataChanged = () => fetchData();
    window.addEventListener("xclean:data-changed", handleDataChanged);
    return () => {
      window.removeEventListener("xclean:data-changed", handleDataChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, location, view]);

  useEffect(() => {
    axios.get(`${API}/services`, { withCredentials: true }).then(r => setServices(r.data)).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateFrom = view === "month" 
        ? format(startOfMonth(currentDate), "yyyy-MM-dd")
        : view === "week"
          ? format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd")
          : format(currentDate, "yyyy-MM-dd");
      
      const dateTo = view === "month"
        ? format(endOfMonth(currentDate), "yyyy-MM-dd")
        : view === "week"
          ? format(endOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd")
          : format(currentDate, "yyyy-MM-dd");

      const [bookingsRes, workersRes] = await Promise.all([
        axios.get(`${API}/bookings`, { 
          params: { date_from: dateFrom, date_to: dateTo, location: location !== "all" ? location : undefined },
          withCredentials: true 
        }),
        axios.get(`${API}/workers`, { 
          params: { location: location !== "all" ? location : undefined },
          withCredentials: true 
        })
      ]);
      
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setWorkers(Array.isArray(workersRes.data) ? workersRes.data : []);
      
      // Initialize previousBookingIds for polling
      if (previousBookingIds.current.size === 0) {
        const safeBookings = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];
        previousBookingIds.current = new Set(safeBookings.map(b => b.booking_id));
      }
    } catch (error) {
      toast.error("Hiba az adatok betöltésekor");
    }
    setLoading(false);
  };

  const navigate = (direction) => {
    if (view === "month") {
      setCurrentDate(direction === "next" ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (view === "week") {
      setCurrentDate(direction === "next" ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === "next" ? addDays(currentDate, 1) : addDays(currentDate, -1));
    }
  };

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setEditForm({ ...booking });
    setEditMode(false);
  };

  const updateBooking = async (updates) => {
    try {
      await axios.put(`${API}/bookings/${selectedBooking.booking_id}`, updates, { withCredentials: true });
      toast.success("Foglalás frissítve");
      fetchData();
      if (updates.status) {
        setSelectedBooking({ ...selectedBooking, ...updates });
      }
    } catch (error) {
      toast.error("Hiba a frissítéskor");
    }
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/bookings/${selectedBooking.booking_id}`, editForm, { withCredentials: true });
      toast.success("Foglalás mentve");
      fetchData();
      setSelectedBooking({ ...selectedBooking, ...editForm });
      setEditMode(false);
    } catch (error) {
      toast.error("Hiba a mentéskor");
    }
  };

  const deleteBooking = async () => {
    try {
      await axios.delete(`${API}/bookings/${selectedBooking.booking_id}`, { withCredentials: true });
      toast.success("Foglalás törölve");
      fetchData();
      setSelectedBooking(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error("Hiba a törléskor");
    }
  };

  const addToBlacklist = async () => {
    if (!blacklistReason.trim()) {
      toast.error("Kérjük adja meg az indoklást");
      return;
    }
    try {
      await axios.post(`${API}/blacklist`, {
        plate_number: selectedBooking.plate_number,
        reason: blacklistReason,
        evidence_images: blacklistImages
      }, { withCredentials: true });
      toast.success("Ügyfél hozzáadva a tiltólistához");
      setShowBlacklistDialog(false);
      setBlacklistReason("");
      setBlacklistImages([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hiba a tiltólistához adáskor");
    }
  };

  const handleBlacklistImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    
    setUploadingBlacklistImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBlacklistImages(prev => [...prev, uploadRes.data.url]);
      toast.success("Kép feltöltve!");
    } catch (error) {
      toast.error("Hiba a kép feltöltésekor");
    } finally {
      setUploadingBlacklistImage(false);
    }
  };

  const removeBlacklistImage = (index) => {
    setBlacklistImages(prev => prev.filter((_, i) => i !== index));
  };

  // DnD handlers
  const handleDragStart = (e, booking) => {
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", booking.booking_id);
  };

  const handleDragOver = (e, date, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const key = `${format(date, "yyyy-MM-dd")}-${hour}`;
    if (!dropTarget || dropTarget.key !== key) {
      setDropTarget({ date, hour, key });
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, date, hour) => {
    e.preventDefault();
    setDropTarget(null);
    if (!draggedBooking) return;

    const newDate = format(date, "yyyy-MM-dd");
    const newTimeSlot = `${hour.toString().padStart(2, "0")}:00`;

    // Skip if nothing changed
    if (draggedBooking.date === newDate && draggedBooking.time_slot?.startsWith(`${hour.toString().padStart(2, "0")}:`)) {
      setDraggedBooking(null);
      return;
    }

    try {
      await axios.put(
        `${API}/bookings/${draggedBooking.booking_id}`,
        { date: newDate, time_slot: newTimeSlot },
        { withCredentials: true }
      );
      toast.success(`${draggedBooking.customer_name} átütemezve → ${newDate} ${newTimeSlot}`);
      fetchData();
    } catch (err) {
      toast.error("Hiba az átütemezéskor");
    }
    setDraggedBooking(null);
  };

  const handleDragEnd = () => {
    setDraggedBooking(null);
    setDropTarget(null);
  };

  const getBookingsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return bookings.filter(b => b.date === dateStr && (selectedWorker === "all" || b.worker_id === selectedWorker));
  };

  const getServiceLabel = (booking) => booking?.service_name || booking?.service || "—";
  const getNotesLabel = (booking) => (booking?.notes && booking.notes.trim().length > 0 ? booking.notes : null);
  const getPriceLabel = (booking) => `${(Number(booking?.price) || 0).toLocaleString()} Ft`;

  const getBookingsForSlot = (date, hour) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const hourStr = `${hour.toString().padStart(2, "0")}:`;
    return bookings.filter(b => 
      b.date === dateStr && 
      b.time_slot?.startsWith(hourStr) &&
      (selectedWorker === "all" || b.worker_id === selectedWorker)
    );
  };

  const getBookingsForWorkerSlot = (date, hour, workerId) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const hourStr = `${hour.toString().padStart(2, "0")}:`;
    return bookings.filter(b => 
      b.date === dateStr && 
      b.time_slot?.startsWith(hourStr) &&
      (workerId === "unassigned" ? !b.worker_id : b.worker_id === workerId)
    );
  };

  const renderTitle = () => {
    if (view === "month") return format(currentDate, "yyyy MMMM", { locale: hu });
    if (view === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, "MMM d", { locale: hu })} - ${format(end, "MMM d, yyyy", { locale: hu })}`;
    }
    return format(currentDate, "yyyy. MMMM d. (EEEE)", { locale: hu });
  };

  // Standard Day View
  const renderDayView = () => {
    const hours = Array.from({ length: 12 }, (_, i) => i + 8);
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="grid grid-cols-[60px_1fr] sm:grid-cols-[80px_1fr] divide-x divide-slate-800">
          <div className="bg-slate-950/50" />
          <div className="p-3 bg-slate-950/50 text-center">
            <div className="text-white font-medium">{format(currentDate, "EEEE", { locale: hu })}</div>
            <div className="text-2xl font-bold text-green-400">{format(currentDate, "d")}</div>
          </div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {hours.map(hour => {
            const slotBookings = getBookingsForSlot(currentDate, hour);
            const isDropTarget = dropTarget?.key === `${format(currentDate, "yyyy-MM-dd")}-${hour}`;
            return (
              <div
                key={hour}
                className={`grid grid-cols-[60px_1fr] sm:grid-cols-[80px_1fr] divide-x divide-slate-800 border-t border-slate-800 min-h-[60px] transition-colors ${isDropTarget ? 'bg-green-500/10' : ''}`}
                onDragOver={(e) => handleDragOver(e, currentDate, hour)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, currentDate, hour)}
              >
                <div className="p-2 text-xs text-slate-500 text-right pr-3">{hour}:00</div>
                <div className="p-1 space-y-1">
                  {isDropTarget && draggedBooking && (
                    <div className="p-2 rounded-lg border border-dashed border-green-500/50 bg-green-500/10 text-xs text-green-400 opacity-70">
                      → {draggedBooking.customer_name}
                    </div>
                  )}
                  {slotBookings.map(booking => (
                    <div
                      key={booking.booking_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, booking)}
                      onDragEnd={handleDragEnd}
                      className={`p-2 rounded-lg border cursor-grab active:cursor-grabbing text-xs select-none ${STATUS_COLORS[booking.status]} ${draggedBooking?.booking_id === booking.booking_id ? 'opacity-40' : ''}`}
                      onClick={() => openBookingDetails(booking)}
                    >
                      <div className="font-medium truncate">{booking.customer_name}</div>
                      <div className="text-slate-300 truncate">{booking.time_slot} · {getPriceLabel(booking)}</div>
                      <div className="text-slate-300 truncate">{booking.plate_number}</div>
                      <div className="text-slate-400 truncate">{getServiceLabel(booking)}</div>
                      {getNotesLabel(booking) && (
                        <div className="text-slate-500 truncate">Megj.: {getNotesLabel(booking)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Per-Worker Day View (columns per worker)
  const renderWorkersDayView = () => {
    const hours = Array.from({ length: 12 }, (_, i) => i + 8);
    const displayWorkers = workers.length > 0 ? workers : [];
    const hasUnassigned = bookings.some(b => !b.worker_id && b.date === format(currentDate, "yyyy-MM-dd"));
    
    // Calculate column count for responsive grid
    const workerCount = displayWorkers.length + (hasUnassigned ? 1 : 0);
    
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {/* Scrollable wrapper for both header and body so they scroll together */}
        <div className="overflow-x-auto -mx-2 px-2">
          <div className="min-w-max">
            {/* Header row with workers */}
            <div className="flex divide-x divide-slate-800">
              <div className="w-14 sm:w-16 flex-shrink-0 bg-slate-950/50 p-2 text-center">
                <div className="text-xs text-slate-500">{format(currentDate, "EEE", { locale: hu })}</div>
                <div className="text-lg font-bold text-green-400">{format(currentDate, "d")}</div>
              </div>
              {displayWorkers.map((worker, idx) => (
                <div
                  key={worker.worker_id}
                  className={`min-w-[110px] sm:min-w-[150px] flex-1 p-2 text-center bg-gradient-to-r ${WORKER_COLORS[idx % WORKER_COLORS.length]}`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <User className="w-3 h-3 text-slate-400" />
                    <span className="text-white font-medium text-sm truncate">{worker.name}</span>
                  </div>
                </div>
              ))}
              {hasUnassigned && (
                <div className="min-w-[110px] sm:min-w-[150px] flex-1 p-2 text-center bg-gradient-to-r from-orange-500/20 to-yellow-500/20">
                  <div className="flex items-center justify-center gap-1">
                    <User className="w-3 h-3 text-orange-400" />
                    <span className="text-orange-300 font-medium text-sm">Nincs hozzárendelve</span>
                  </div>
                </div>
              )}
            </div>

            {/* Time slots */}
            <div className="max-h-[500px] overflow-y-auto">
              {hours.map(hour => (
                <div key={hour} className="flex divide-x divide-slate-800 border-t border-slate-800">
                  <div className="w-14 sm:w-16 flex-shrink-0 p-1 text-xs text-slate-500 text-right pr-2">
                    {hour}:00
                  </div>
                  {displayWorkers.map((worker) => {
                    const workerBookings = getBookingsForWorkerSlot(currentDate, hour, worker.worker_id);
                    const isWT = dropTarget?.key === `${format(currentDate, "yyyy-MM-dd")}-${hour}-${worker.worker_id}`;
                    return (
                      <div
                        key={worker.worker_id}
                        className={`min-w-[110px] sm:min-w-[150px] flex-1 p-1 min-h-[50px] transition-colors ${isWT ? 'bg-green-500/10' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setDropTarget({ date: currentDate, hour, key: `${format(currentDate, "yyyy-MM-dd")}-${hour}-${worker.worker_id}` }); }}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, currentDate, hour)}
                      >
                        {workerBookings.map(booking => (
                          <div
                            key={booking.booking_id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, booking)}
                            onDragEnd={handleDragEnd}
                            className={`p-1.5 rounded text-xs cursor-grab active:cursor-grabbing mb-1 select-none ${STATUS_COLORS[booking.status]} ${draggedBooking?.booking_id === booking.booking_id ? 'opacity-40' : ''}`}
                            onClick={() => openBookingDetails(booking)}
                          >
                            <div className="font-medium truncate">{booking.customer_name}</div>
                            <div className="text-slate-300 truncate text-[10px]">{booking.time_slot} · {getPriceLabel(booking)}</div>
                            <div className="text-slate-400 truncate text-[10px]">{booking.plate_number}</div>
                            <div className="text-slate-400 truncate text-[10px]">{getServiceLabel(booking)}</div>
                            {getNotesLabel(booking) && (
                              <div className="text-slate-500 truncate text-[10px]">Megj.: {getNotesLabel(booking)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {hasUnassigned && (
                    <div className="min-w-[110px] sm:min-w-[150px] flex-1 p-1 min-h-[50px]">
                      {getBookingsForWorkerSlot(currentDate, hour, "unassigned").map(booking => (
                        <div
                          key={booking.booking_id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, booking)}
                          onDragEnd={handleDragEnd}
                          className={`p-1.5 rounded text-xs cursor-grab active:cursor-grabbing mb-1 border-orange-500/30 bg-orange-500/10 text-orange-300 select-none ${draggedBooking?.booking_id === booking.booking_id ? 'opacity-40' : ''}`}
                          onClick={() => openBookingDetails(booking)}
                        >
                          <div className="font-medium truncate">{booking.customer_name}</div>
                          <div className="text-orange-300/80 truncate text-[10px]">{booking.time_slot} · {getPriceLabel(booking)}</div>
                          <div className="text-orange-400/70 truncate text-[10px]">{booking.plate_number}</div>
                          <div className="text-orange-400/70 truncate text-[10px]">{getServiceLabel(booking)}</div>
                          {getNotesLabel(booking) && (
                            <div className="text-orange-300/70 truncate text-[10px]">Megj.: {getNotesLabel(booking)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Standard Week View
  const renderWeekView = () => {
    const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
    const hours = Array.from({ length: 12 }, (_, i) => i + 8);
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {/* Desktop view */}
        <div className="hidden sm:block">
          <div className="grid grid-cols-[70px_repeat(7,1fr)] divide-x divide-slate-800">
            <div className="bg-slate-950/50" />
            {days.map(day => (
              <div key={day.toISOString()} className={`p-2 text-center bg-slate-950/50 ${isSameDay(day, new Date()) ? 'bg-green-500/10' : ''}`}>
                <div className="text-xs text-slate-500">{format(day, "EEE", { locale: hu })}</div>
                <div className={`text-lg font-bold ${isSameDay(day, new Date()) ? 'text-green-400' : 'text-white'}`}>{format(day, "d")}</div>
              </div>
            ))}
          </div>
          <div className="max-h-[680px] overflow-y-auto">
            {hours.map(hour => (
              <div key={hour} className="grid grid-cols-[70px_repeat(7,1fr)] divide-x divide-slate-800 border-t border-slate-800 min-h-[72px]">
                <div className="p-1 text-xs text-slate-500 text-right pr-2 pt-2">{hour}:00</div>
                {days.map(day => {
                  const slotBookings = getBookingsForSlot(day, hour);
                  const isWT = dropTarget?.key === `${format(day, "yyyy-MM-dd")}-${hour}`;
                  return (
                    <div
                      key={day.toISOString()}
                      className={`p-0.5 overflow-hidden transition-colors ${isWT ? 'bg-green-500/10' : ''}`}
                      onDragOver={(e) => handleDragOver(e, day, hour)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, day, hour)}
                    >
                      <div className="max-h-[68px] overflow-y-auto pr-0.5">
                        {slotBookings.map(booking => (
                          <div
                            key={booking.booking_id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, booking)}
                            onDragEnd={handleDragEnd}
                            className={`px-1.5 py-0.5 mb-0.5 rounded text-[10px] cursor-grab active:cursor-grabbing border-l-2 select-none ${STATUS_COLORS[booking.status]} hover:brightness-110 ${draggedBooking?.booking_id === booking.booking_id ? 'opacity-40' : ''}`}
                            onClick={() => openBookingDetails(booking)}
                            title={`${booking.time_slot} · ${getPriceLabel(booking)} · ${booking.customer_name} · ${booking.plate_number} · ${getServiceLabel(booking)}${getNotesLabel(booking) ? ` · Megj.: ${getNotesLabel(booking)}` : ""}`}
                          >
                            <div className="font-semibold truncate">{booking.customer_name}</div>
                            <div className="truncate">{booking.time_slot} · {getPriceLabel(booking)}</div>
                            <div className="truncate">{booking.plate_number}</div>
                            <div className="truncate text-[9px] opacity-90">{getServiceLabel(booking)}</div>
                            {getNotesLabel(booking) && <div className="truncate text-[9px] opacity-75">Megj.: {getNotesLabel(booking)}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        
        {/* Mobile view - day cards */}
        <div className="sm:hidden space-y-2 p-2">
          {days.map(day => {
            const dayBookings = getBookingsForDate(day);
            return (
              <div key={day.toISOString()} className={`rounded-lg border ${isSameDay(day, new Date()) ? 'border-green-500/50 bg-green-500/5' : 'border-slate-700'}`}>
                <div className={`p-2 border-b ${isSameDay(day, new Date()) ? 'border-green-500/30' : 'border-slate-700'} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${isSameDay(day, new Date()) ? 'text-green-400' : 'text-white'}`}>
                      {format(day, "EEEE", { locale: hu })}
                    </span>
                    <span className="text-slate-400 text-sm">{format(day, "d", { locale: hu })}</span>
                  </div>
                  <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                    {dayBookings.length} foglalás
                  </Badge>
                </div>
                {dayBookings.length > 0 ? (
                  <div className="p-2 space-y-1 max-h-[200px] overflow-y-auto">
                    {dayBookings.map(booking => (
                      <div
                        key={booking.booking_id}
                        className={`p-2 rounded-lg text-xs cursor-pointer ${STATUS_COLORS[booking.status]}`}
                        onClick={() => openBookingDetails(booking)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{booking.time_slot}</span>
                          <span className="text-slate-400">{getPriceLabel(booking)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-1 gap-2">
                          <span className="truncate">{booking.customer_name}</span>
                          {booking.worker_name && (
                            <span className="text-blue-300 text-[10px]">{booking.worker_name}</span>
                          )}
                        </div>
                        <div className="text-slate-400 mt-1 truncate">{getServiceLabel(booking)}</div>
                        {getNotesLabel(booking) && <div className="text-slate-500 mt-0.5 truncate">Megj.: {getNotesLabel(booking)}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 text-center text-slate-500 text-sm">
                    Nincs foglalás
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Month View
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const weeks = [];
    let day = calendarStart;
    while (day <= calendarEnd) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(day));
        day = addDays(day, 1);
      }
      weeks.push(week);
    }
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {/* Desktop view */}
        <div className="hidden sm:block">
          <div className="grid grid-cols-7 divide-x divide-slate-800 bg-slate-950/50">
            {["H", "K", "Sz", "Cs", "P", "Szo", "V"].map(d => (
              <div key={d} className="p-2 text-center text-xs text-slate-500 font-medium">{d}</div>
            ))}
          </div>
          {weeks.map((week, i) => (
            <div key={i} className="grid grid-cols-7 divide-x divide-slate-800 border-t border-slate-800">
              {week.map(day => {
                const dayBookings = getBookingsForDate(day);
                const isToday = isSameDay(day, new Date());
                const isCurrentMonth = isSameMonth(day, currentDate);
                return (
                  <div 
                    key={day.toISOString()} 
                    className={`min-h-[130px] p-1.5 cursor-pointer hover:bg-slate-800/30 transition-colors ${!isCurrentMonth ? 'bg-slate-950/30' : ''} ${isToday ? 'bg-green-500/5 ring-1 ring-inset ring-green-500/30' : ''}`}
                    onClick={() => { setCurrentDate(day); setView("day"); }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-semibold ${isToday ? 'text-green-400' : isCurrentMonth ? 'text-white' : 'text-slate-600'}`}>{format(day, "d")}</span>
                      {dayBookings.length > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${dayBookings.length > 3 ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-700 text-slate-300'}`}>
                          {dayBookings.length}
                        </span>
                      )}
                    </div>
                    <div className="max-h-[116px] overflow-y-auto pr-0.5 space-y-0.5">
                      {dayBookings.map(booking => (
                        <div 
                          key={booking.booking_id} 
                          className={`text-[10px] px-1 py-0.5 rounded border-l-2 truncate ${STATUS_COLORS[booking.status]}`} 
                          onClick={(e) => { e.stopPropagation(); openBookingDetails(booking); }}
                          title={`${booking.time_slot} · ${getPriceLabel(booking)} · ${booking.customer_name} · ${booking.plate_number} · ${getServiceLabel(booking)}${getNotesLabel(booking) ? ` · Megj.: ${getNotesLabel(booking)}` : ""}`}
                        >
                          <div className="font-medium truncate">{booking.customer_name}</div>
                          <div className="truncate">{booking.time_slot} · {getPriceLabel(booking)}</div>
                          <div className="truncate">{booking.plate_number}</div>
                          <div className="truncate opacity-90">{getServiceLabel(booking)}</div>
                          {getNotesLabel(booking) && <div className="truncate opacity-75">Megj.: {getNotesLabel(booking)}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        
        {/* Mobile view - compact list */}
        <div className="sm:hidden">
          <div className="grid grid-cols-7 bg-slate-950/50">
            {["H", "K", "Sz", "Cs", "P", "Szo", "V"].map((d, idx) => (
              <div key={idx} className="p-1 text-center text-[10px] text-slate-500 font-medium">{d}</div>
            ))}
          </div>
          {weeks.map((week, i) => (
            <div key={i} className="grid grid-cols-7 border-t border-slate-800">
              {week.map(day => {
                const dayBookings = getBookingsForDate(day);
                const isToday = isSameDay(day, new Date());
                const isCurrentMonth = isSameMonth(day, currentDate);
                return (
                  <div 
                    key={day.toISOString()} 
                    className={`min-h-[50px] p-1 border-r border-slate-800 last:border-r-0 ${!isCurrentMonth ? 'bg-slate-950/30' : ''} ${isToday ? 'bg-green-500/10' : ''}`}
                    onClick={() => {
                      if (dayBookings.length > 0) {
                        setCurrentDate(day);
                        setView("day");
                      }
                    }}
                  >
                    <div className={`text-[10px] font-medium ${isToday ? 'text-green-400' : isCurrentMonth ? 'text-white' : 'text-slate-600'}`}>
                      {format(day, "d")}
                    </div>
                    {dayBookings.length > 0 && (
                      <div className="mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-green-500 mx-auto" title={`${dayBookings.length} foglalás`} />
                        {dayBookings.length > 1 && (
                          <div className="text-[8px] text-center text-slate-400">+{dayBookings.length - 1}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const todayIso = format(new Date(), "yyyy-MM-dd");
  const todayBookingCount = bookings.filter((b) => b.date === todayIso).length;
  const locationLabel = location === "all" ? "Minden telephely" : location;

  return (
    <div className={`${studioPageRootClass(ui, "calendar")} ${ui === "studio" ? "xclean-calendar-root" : ""}`} data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 sm:w-7 sm:h-7 text-green-400" />
          Foglalási naptár
        </h1>

        {ui === "studio" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card className="glass-card">
              <CardContent className="p-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Mai foglalás</p>
                <p className="text-2xl font-bold text-white tabular-nums mt-1">{todayBookingCount}</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Nézetben összesen</p>
                <p className="text-2xl font-bold text-white tabular-nums mt-1">{bookings.length}</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Telephely</p>
                <p className="text-base font-semibold text-cyan-300 mt-2">{locationLabel}</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Search box */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Keresés: ügyfél neve vagy rendszám..."
            className="pl-10 bg-slate-900 border-slate-700 text-white w-full sm:w-80"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">
          {/* Location filter */}
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="w-full sm:w-36 bg-slate-900 border-slate-700 text-white text-sm">
              <MapPin className="w-4 h-4 mr-1 sm:mr-2 text-green-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {LOCATIONS.map(loc => (
                <SelectItem key={loc} value={loc} className="text-white">{loc === "all" ? "Minden telephely" : loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Worker filter - only shown when not in workers view mode */}
          {viewMode !== "workers" && (
            <Select value={selectedWorker} onValueChange={setSelectedWorker}>
              <SelectTrigger className="w-full sm:w-40 bg-slate-900 border-slate-700 text-white text-sm">
                <User className="w-4 h-4 mr-1 sm:mr-2 text-green-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all" className="text-white">Minden dolgozó</SelectItem>
                {workers.map(w => (
                  <SelectItem key={w.worker_id} value={w.worker_id} className="text-white">{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Time period view selector */}
          <div className="flex bg-slate-900 rounded-lg border border-slate-700 p-1">
            {[
              { id: "day", label: "Nap" },
              { id: "week", label: "Hét" },
              { id: "month", label: "Hónap" },
              { id: "list", label: "Lista" },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => { setView(v.id); if (v.id !== "day") setViewMode("standard"); }}
                className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm ${view === v.id ? 'bg-green-500 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          
          {/* View mode toggle - only for day view */}
          <Button
            type="button"
            onClick={openNewBookingDialog}
            className="bg-green-600 hover:bg-green-500 text-white w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Új foglalás
          </Button>

          {view === "day" && (
            <div className="flex bg-slate-900 rounded-lg border border-slate-700 p-1">
              <button 
                onClick={() => setViewMode("standard")} 
                className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm flex items-center gap-1 ${viewMode === "standard" ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'}`}
                title="Standard nézet"
              >
                <Grid3X3 className="w-3 h-3" />
                <span className="hidden sm:inline">Standard</span>
              </button>
              <button 
                onClick={() => setViewMode("workers")} 
                className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm flex items-center gap-1 ${viewMode === "workers" ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'}`}
                title="Munkásonkénti nézet"
              >
                <Columns3 className="w-3 h-3" />
                <span className="hidden sm:inline">Munkások</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navigate("prev")} className="border-slate-700 text-white px-2 sm:px-3">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-sm sm:text-lg font-semibold text-white capitalize">{renderTitle()}</h2>
        <Button variant="outline" size="sm" onClick={() => navigate("next")} className="border-slate-700 text-white px-2 sm:px-3">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Status legend */}
      <div className="flex flex-wrap gap-1 sm:gap-2">
        {Object.entries(STATUS_LABELS).map(([status, label]) => (
          <Badge key={status} className={`${STATUS_COLORS[status]} text-[10px] sm:text-xs px-1.5 sm:px-2`}>{label}</Badge>
        ))}
      </div>

      <div>
          {/* Search results (replaces calendar when active) */}
          {searchTerm.trim() ? (
            (() => {
              const q = searchTerm.trim().toLowerCase();
              const compactQ = q.replace(/\s/g, "");
              const results = bookings.filter((b) => {
                const customerName = String(b?.customer_name || "").toLowerCase();
                const plateNumber = String(b?.plate_number || "").toLowerCase().replace(/\s/g, "");
                return customerName.includes(q) || plateNumber.includes(compactQ);
              });
              return (
                <div className="space-y-2">
                  <p className="text-slate-400 text-sm">
                    {results.length === 0
                      ? "Nincs találat"
                      : `${results.length} találat: „${searchTerm}"`}
                  </p>
                  {results.map(booking => {
                    const sc = getStatusConfig(booking.status);
                    return (
                      <div
                        key={booking.booking_id}
                        className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-green-500/30 transition-colors cursor-pointer"
                        onClick={() => openBookingDetails(booking)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                            <Car className="w-4 h-4 text-green-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-semibold truncate">{booking.customer_name}</p>
                            <p className="text-slate-400 text-xs flex items-center gap-2">
                              <span className="font-mono">{booking.plate_number}</span>
                              <span>·</span>
                              <span>{booking.service_name}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right text-xs text-slate-400">
                            <p>{booking.date} {booking.time_slot}</p>
                            <p>{booking.location}</p>
                          </div>
                          <Badge className={`${sc.bg} ${sc.text} text-xs`}>{sc.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            /* Calendar content */
            loading ? (
              <div className="text-center py-20 text-slate-500">Betöltés...</div>
            ) : view === "list" ? (
              /* List view — mobile-friendly chronological cards grouped by date */
              (() => {
                const sorted = [...bookings]
                  .filter(b => b.status !== "lemondta" && b.status !== "lemondva" && b.status !== "torolt")
                  .sort((a, b) => {
                    const dt = (x) => `${x.date}T${x.time_slot || "00:00"}`;
                    return dt(a) < dt(b) ? -1 : 1;
                  });
                const byDate = sorted.reduce((acc, b) => {
                  (acc[b.date] = acc[b.date] || []).push(b);
                  return acc;
                }, {});
                if (sorted.length === 0) return (
                  <div className="text-center py-20 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nincs foglalás ebben az időszakban</p>
                  </div>
                );
                return (
                  <div className="space-y-4">
                    {Object.entries(byDate).map(([date, dayBookings]) => {
                      const d = new Date(date + "T00:00:00");
                      const dayLabel = format(d, "EEEE, MMMM d.", { locale: hu });
                      return (
                        <div key={date}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="h-px flex-1 bg-slate-800" />
                            <span className="text-slate-400 text-xs font-medium px-2 capitalize">{dayLabel}</span>
                            <span className="text-slate-600 text-xs">{dayBookings.length} db</span>
                            <div className="h-px flex-1 bg-slate-800" />
                          </div>
                          <div className="space-y-2">
                            {dayBookings.map(booking => {
                              const sc = getStatusConfig(booking.status);
                              return (
                                <div
                                  key={booking.booking_id}
                                  className="bg-slate-900 border border-slate-800 rounded-xl p-3 flex items-center justify-between gap-3 hover:border-green-500/30 transition-colors cursor-pointer"
                                  onClick={() => openBookingDetails(booking)}
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="text-center w-12 flex-shrink-0">
                                      <p className="text-green-400 font-bold text-sm">{booking.time_slot}</p>
                                      <p className="text-slate-600 text-[10px]">{booking.location?.slice(0,3)}</p>
                                    </div>
                                    <div className="w-px h-8 bg-slate-700 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-white font-medium text-sm truncate">{booking.customer_name}</p>
                                      <p className="text-slate-400 text-xs truncate">
                                        <span className="font-mono">{booking.plate_number}</span>
                                        <span className="mx-1">·</span>
                                        <span>{booking.service_name}</span>
                                      </p>
                                    </div>
                                  </div>
                                  <Badge className={`${sc.bg} ${sc.text} text-xs flex-shrink-0`}>{sc.label}</Badge>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <>
                {view === "day" && viewMode === "standard" && renderDayView()}
                {view === "day" && viewMode === "workers" && renderWorkersDayView()}
                {view === "week" && renderWeekView()}
                {view === "month" && renderMonthView()}
              </>
            )
          )}
        </div>

      {/* Booking Details Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => { setSelectedBooking(null); setEditMode(false); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Car className="w-5 h-5 text-green-400" />
                {editMode ? "Foglalás szerkesztése" : "Foglalás részletei"}
              </span>
              {!editMode && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="border-slate-700">
                    <Edit className="w-4 h-4 mr-1" /> Szerkesztés
                  </Button>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && !editMode && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                <p className="text-xs uppercase tracking-wide text-cyan-300 mb-2">Munka részletek</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><label className="text-slate-500">Szolgáltatás</label><p>{selectedBooking.service_name}</p></div>
                  <div><label className="text-slate-500">Autó</label><p>{selectedBooking.car_type}</p></div>
                  <div><label className="text-slate-500">Időpont</label><p>{selectedBooking.date} {selectedBooking.time_slot}</p></div>
                  <div><label className="text-slate-500">Telephely</label><p>{selectedBooking.location}</p></div>
                  <div className="col-span-2"><label className="text-slate-500">Ár</label><p className="text-green-400 font-bold text-lg">{selectedBooking.price?.toLocaleString()} Ft</p></div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                <p className="text-xs uppercase tracking-wide text-blue-300 mb-2">Ügyfél információk</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><label className="text-slate-500">Ügyfél</label><p className="font-medium">{selectedBooking.customer_name}</p></div>
                  <div><label className="text-slate-500">Rendszám</label><p className="font-mono font-medium">{selectedBooking.plate_number}</p></div>
                  <div><label className="text-slate-500">Telefon</label><p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {selectedBooking.phone}</p></div>
                  <div><label className="text-slate-500">Email</label><p className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {selectedBooking.email}</p></div>
                </div>
              </div>

              <div>
                <label className="text-slate-500 text-sm block mb-1">Dolgozó</label>
                <Select value={selectedBooking.worker_id || "none"} onValueChange={(v) => updateBooking({ worker_id: v === "none" ? null : v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700"><SelectValue placeholder="Nincs hozzárendelve" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="none" className="text-slate-400">Nincs hozzárendelve</SelectItem>
                    {workers.map(w => (<SelectItem key={w.worker_id} value={w.worker_id} className="text-white">{w.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-slate-500 text-sm block mb-2">Státusz</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_LABELS).map(([status, label]) => (
                    <Button key={status} size="sm" variant={selectedBooking.status === status ? "default" : "outline"}
                      className={selectedBooking.status === status ? "bg-green-600" : "border-slate-700"}
                      onClick={() => updateBooking({ status })}>
                      {status === "kesz" && <Check className="w-3 h-3 mr-1" />}
                      {status === "nem_jott_el" && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {status === "lemondta" && <X className="w-3 h-3 mr-1" />}
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {selectedBooking.notes && (
                <div><label className="text-slate-500 text-sm">Megjegyzés</label><p className="text-sm mt-1 bg-slate-950/50 p-2 rounded">{selectedBooking.notes}</p></div>
              )}

              <div className="flex gap-2 pt-4 border-t border-slate-800">
                <Button size="sm" variant="destructive" onClick={() => setShowDeleteConfirm(true)} className="bg-red-600 hover:bg-red-700">
                  <Trash2 className="w-4 h-4 mr-1" /> Törlés
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowBlacklistDialog(true)} className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
                  <Ban className="w-4 h-4 mr-1" /> Tiltólistára
                </Button>
              </div>
            </div>
          )}

          {selectedBooking && editMode && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-500 text-sm">Ügyfél neve</label>
                  <Input value={editForm.customer_name || ""} onChange={e => setEditForm({...editForm, customer_name: e.target.value})} className="bg-slate-950 border-slate-700 text-white mt-1" />
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Rendszám</label>
                  <Input value={editForm.plate_number || ""} onChange={e => setEditForm({...editForm, plate_number: e.target.value.toUpperCase()})} className="bg-slate-950 border-slate-700 text-white mt-1 uppercase" />
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Autó típusa</label>
                  <Input value={editForm.car_type || ""} onChange={e => setEditForm({...editForm, car_type: e.target.value})} className="bg-slate-950 border-slate-700 text-white mt-1" />
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Szolgáltatás</label>
                  <Select value={editForm.service_id || ""} onValueChange={v => setEditForm({...editForm, service_id: v})}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                      {services.map(s => (<SelectItem key={s.service_id} value={s.service_id} className="text-white">{s.name} - {s.price?.toLocaleString()} Ft</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Dátum</label>
                  <Input type="date" value={editForm.date || ""} onChange={e => setEditForm({...editForm, date: e.target.value})} className="bg-slate-950 border-slate-700 text-white mt-1" />
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Időpont</label>
                  <Input value={editForm.time_slot || ""} onChange={e => setEditForm({...editForm, time_slot: e.target.value})} placeholder="10:00" className="bg-slate-950 border-slate-700 text-white mt-1" />
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Telefon</label>
                  <Input value={editForm.phone || ""} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="bg-slate-950 border-slate-700 text-white mt-1" />
                </div>
                <div>
                  <label className="text-slate-500 text-sm">Email</label>
                  <Input value={editForm.email || ""} onChange={e => setEditForm({...editForm, email: e.target.value})} className="bg-slate-950 border-slate-700 text-white mt-1" />
                </div>
              </div>
              <div>
                <label className="text-slate-500 text-sm">Megjegyzés</label>
                <textarea value={editForm.notes || ""} onChange={e => setEditForm({...editForm, notes: e.target.value})} className="w-full bg-slate-950 border border-slate-700 text-white rounded-lg p-2 text-sm mt-1 h-20" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditMode(false)} className="border-slate-700">Mégse</Button>
                <Button onClick={saveEdit} className="bg-green-600 hover:bg-green-700"><Save className="w-4 h-4 mr-1" /> Mentés</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-red-400">Foglalás törlése</DialogTitle></DialogHeader>
          <p className="text-slate-400">Biztosan törölni szeretnéd ezt a foglalást?</p>
          <p className="text-white font-medium">{selectedBooking?.customer_name} - {selectedBooking?.date} {selectedBooking?.time_slot}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="border-slate-700">Mégse</Button>
            <Button variant="destructive" onClick={deleteBooking} className="bg-red-600 hover:bg-red-700">Törlés</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blacklist Dialog */}
      {/* Új foglalás */}
      <Dialog open={showNewBooking} onOpenChange={setShowNewBooking}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-green-400" />
              Új foglalás
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Telephely</Label>
                <Select
                  value={newBooking.nb_location}
                  onValueChange={(v) => setNewBooking((s) => ({ ...s, nb_location: v }))}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="Debrecen" className="text-white">Debrecen</SelectItem>
                    <SelectItem value="Budapest" className="text-white">Budapest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Dátum</Label>
                <Input
                  type="date"
                  value={newBooking.date}
                  onChange={(e) => setNewBooking((s) => ({ ...s, date: e.target.value, time_slot: "" }))}
                  className="bg-slate-950 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Szolgáltatás</Label>
              <Select
                value={newBooking.service_id}
                onValueChange={(v) => setNewBooking((s) => ({ ...s, service_id: v }))}
              >
                <SelectTrigger className="bg-slate-950 border-slate-700 mt-1">
                  <SelectValue placeholder="Válassz" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                  {services.map((s) => (
                    <SelectItem key={s.service_id} value={s.service_id} className="text-white">
                      {s.name} — {(s.price ?? 0).toLocaleString()} Ft
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Időpont</Label>
              {nbLoadingSlots ? (
                <p className="text-slate-500 text-sm mt-2">Slotok betöltése…</p>
              ) : (
                <Select
                  value={newBooking.time_slot}
                  onValueChange={(v) => setNewBooking((s) => ({ ...s, time_slot: v }))}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-700 mt-1">
                    <SelectValue placeholder="Válassz időpontot" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                    {nbSlots
                      .filter((sl) => sl.is_available)
                      .map((sl) => (
                        <SelectItem key={sl.time_slot} value={sl.time_slot} className="text-white">
                          {sl.time_slot}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Ügyfél neve *</Label>
                <Input
                  value={newBooking.customer_name}
                  onChange={(e) => setNewBooking((s) => ({ ...s, customer_name: e.target.value }))}
                  className="bg-slate-950 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Rendszám *</Label>
                <Input
                  value={newBooking.plate_number}
                  onChange={(e) =>
                    setNewBooking((s) => ({ ...s, plate_number: e.target.value.toUpperCase() }))
                  }
                  className="bg-slate-950 border-slate-700 text-white mt-1 font-mono"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Telefon *</Label>
                <Input
                  value={newBooking.phone}
                  onChange={(e) => setNewBooking((s) => ({ ...s, phone: e.target.value }))}
                  className="bg-slate-950 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Email *</Label>
                <Input
                  type="email"
                  value={newBooking.email}
                  onChange={(e) => setNewBooking((s) => ({ ...s, email: e.target.value }))}
                  className="bg-slate-950 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Autó típus</Label>
              <Input
                value={newBooking.car_type}
                onChange={(e) => setNewBooking((s) => ({ ...s, car_type: e.target.value }))}
                className="bg-slate-950 border-slate-700 text-white mt-1"
              />
            </div>
            {newBooking.nb_location === "Debrecen" && (
              <>
                <div>
                  <Label className="text-slate-400 text-xs">Dolgozó (Debrecen: Bence / Attila)</Label>
                  <Select
                    value={newBooking.worker_id || "none"}
                    onValueChange={(v) =>
                      setNewBooking((s) => ({
                        ...s,
                        worker_id: v === "none" ? "" : v,
                      }))
                    }
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-700 mt-1">
                      <SelectValue placeholder="Opcionális" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="none" className="text-slate-400">
                        Nincs megadva
                      </SelectItem>
                      {filterWorkersBookingRules(workers).map((w) => (
                        <SelectItem key={w.worker_id} value={w.worker_id} className="text-white">
                          {w.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="nb-shared"
                    checked={newBooking.is_shared_work}
                    onCheckedChange={(c) =>
                      setNewBooking((s) => ({
                        ...s,
                        is_shared_work: !!c,
                        shared_worker_id: c ? s.shared_worker_id : "",
                      }))
                    }
                  />
                  <Label htmlFor="nb-shared" className="text-slate-300 text-sm cursor-pointer">
                    Közös munka (2 dolgozó — jutalék megosztás)
                  </Label>
                </div>
                {newBooking.is_shared_work && (
                  <div>
                    <Label className="text-slate-400 text-xs">Második dolgozó</Label>
                    <Select
                      value={newBooking.shared_worker_id || "none"}
                      onValueChange={(v) =>
                        setNewBooking((s) => ({
                          ...s,
                          shared_worker_id: v === "none" ? "" : v,
                        }))
                      }
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-700 mt-1">
                        <SelectValue placeholder="Válassz" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="none" className="text-slate-400">
                          —
                        </SelectItem>
                        {filterWorkersBookingRules(workers)
                          .filter((w) => w.worker_id !== newBooking.worker_id)
                          .map((w) => (
                            <SelectItem key={w.worker_id} value={w.worker_id} className="text-white">
                              {w.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            {newBooking.nb_location !== "Debrecen" && (
              <div>
                <Label className="text-slate-400 text-xs">Dolgozó</Label>
                <Select
                  value={newBooking.worker_id || "none"}
                  onValueChange={(v) =>
                    setNewBooking((s) => ({ ...s, worker_id: v === "none" ? "" : v }))
                  }
                >
                  <SelectTrigger className="bg-slate-950 border-slate-700 mt-1">
                    <SelectValue placeholder="Opcionális" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="none" className="text-slate-400">
                      Nincs megadva
                    </SelectItem>
                    {workers.map((w) => (
                      <SelectItem key={w.worker_id} value={w.worker_id} className="text-white">
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-slate-400 text-xs">Megjegyzés</Label>
              <textarea
                value={newBooking.notes}
                onChange={(e) => setNewBooking((s) => ({ ...s, notes: e.target.value }))}
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-lg p-2 text-sm mt-1 min-h-[72px]"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNewBooking(false)} className="border-slate-700">
                Mégse
              </Button>
              <Button
                onClick={submitNewBooking}
                disabled={newBookingSubmitting}
                className="bg-green-600 hover:bg-green-500"
              >
                {newBookingSubmitting ? "Mentés…" : "Foglalás létrehozása"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showBlacklistDialog} onOpenChange={(open) => { 
        setShowBlacklistDialog(open); 
        if (!open) { setBlacklistReason(""); setBlacklistImages([]); }
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-400">
              <UserX className="w-5 h-5" /> Tiltólistára helyezés
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-400 text-sm">Az ügyfél ({selectedBooking?.customer_name} - {selectedBooking?.plate_number}) felkerül a tiltólistára és nem tud majd időpontot foglalni.</p>
          <div>
            <label className="text-slate-500 text-sm">Indoklás *</label>
            <textarea value={blacklistReason} onChange={e => setBlacklistReason(e.target.value)} placeholder="Pl: Többszöri meg nem jelenés, fizetés nélkül távozott, stb." className="w-full bg-slate-950 border border-slate-700 text-white rounded-lg p-2 text-sm mt-1 h-20" />
          </div>
          
          {/* Evidence Images Upload */}
          <div>
            <label className="text-slate-500 text-sm flex items-center gap-2">
              <Image className="w-4 h-4" /> Bizonyíték képek (opcionális)
            </label>
            <input 
              type="file" 
              ref={blacklistFileRef} 
              className="hidden" 
              accept="image/*" 
              onChange={handleBlacklistImageUpload}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {blacklistImages.map((url, idx) => (
                <div key={idx} className="relative w-16 h-16 group">
                  <img src={url} alt={`Bizonyíték ${idx + 1}`} className="w-full h-full object-cover rounded-lg border border-slate-700" />
                  <button 
                    onClick={() => removeBlacklistImage(idx)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              <button 
                onClick={() => blacklistFileRef.current?.click()}
                disabled={uploadingBlacklistImage}
                className="w-16 h-16 border-2 border-dashed border-slate-700 rounded-lg flex flex-col items-center justify-center text-slate-500 hover:text-slate-300 hover:border-slate-500 transition-colors"
              >
                {uploadingBlacklistImage ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-500 border-t-orange-400" />
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span className="text-[10px] mt-1">Kép</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-slate-600 text-xs mt-1">Fotók a károkról, problémákról</p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowBlacklistDialog(false); setBlacklistReason(""); setBlacklistImages([]); }} className="border-slate-700">Mégse</Button>
            <Button onClick={addToBlacklist} className="bg-orange-600 hover:bg-orange-700"><Ban className="w-4 h-4 mr-1" /> Tiltólistára</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calendar;
